import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Button, Divider, Drawer, IconButton, List, ListItemButton,
  ListItemIcon, ListItemText, Typography
} from '@mui/material';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import SellOutlinedIcon from '@mui/icons-material/SellOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import Brand from '../Brand';

const drawerWidth = 232;

const MainLayout = ({ children, onLogout, historyVersion }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [fileCount, setFileCount] = useState(0);

  useEffect(() => {
    let active = true;
    axios.get('/api/files').then(response => {
      if (active) setFileCount((response.data.files || []).length);
    }).catch(() => { if (active) setFileCount(0); });
    return () => { active = false; };
  }, [historyVersion]);

  const go = path => {
    navigate(path);
    setMobileOpen(false);
  };
  const logout = async () => {
    try { await axios.post('/api/logout'); } finally { onLogout(); }
  };
  const item = (label, path, Icon, count = false) => (
    <ListItemButton
      key={path}
      selected={location.pathname === path}
      onClick={() => go(path)}
      sx={{
        minHeight: 40, px: 1.25, mb: 0.25, borderRadius: '9px', color: '#cfe3e1',
        '& .MuiListItemIcon-root': { color: 'inherit', minWidth: 34 },
        '&.Mui-selected': { bgcolor: '#15444a', color: '#fff' },
        '&.Mui-selected:hover, &:hover': { bgcolor: '#15444a' }
      }}
    >
      <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
      <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14 }} />
      {count && fileCount > 0 && <Typography sx={{ color: '#cfe3e1', fontSize: 11 }}>{fileCount}</Typography>}
    </ListItemButton>
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#0e2a2f', color: '#cfe3e1', px: 1.75, py: 2 }}>
      <Box sx={{ px: 0.75, pb: 2.25 }}><Brand dark /></Box>
      <List disablePadding>{item('Home', '/', HomeOutlinedIcon)}</List>
      <Typography sx={{ color: '#7fa5a1', fontSize: 11, letterSpacing: 0.5, px: 1.25, mt: 1.5, mb: 0.75 }}>CONVERT</Typography>
      <List disablePadding>
        {item('Pricebook', '/pricebook', SellOutlinedIcon)}
        {item('Inventory', '/inventory', Inventory2OutlinedIcon)}
        {item('Catalog', '/catalog', CategoryOutlinedIcon)}
      </List>
      <Typography sx={{ color: '#7fa5a1', fontSize: 11, letterSpacing: 0.5, px: 1.25, mt: 1.5, mb: 0.75 }}>FILES</Typography>
      <List disablePadding>
        {item('Recent files', '/files', HistoryOutlinedIcon, true)}
        {item('Templates', '/templates', DescriptionOutlinedIcon)}
      </List>
      <Box sx={{ mt: 'auto', pt: 1.5 }}>
        <Divider sx={{ borderColor: 'rgba(207,227,225,.14)', mb: 1.25 }} />
        <Typography sx={{ color: '#7fa5a1', fontSize: 12, mb: 0.5 }}>Deraah Trading Company</Typography>
        <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout} sx={{ px: 1.25, color: '#cfe3e1' }}>Log out</Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Drawer variant="permanent" sx={{ display: { xs: 'none', md: 'block' }, width: drawerWidth, flexShrink: 0, '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 0 } }}>
        {drawer}
      </Drawer>
      <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)} ModalProps={{ keepMounted: true }} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: drawerWidth, border: 0 } }}>
        {drawer}
      </Drawer>
      <Box component="main" sx={{ ml: { xs: 0, md: `${drawerWidth}px` }, minHeight: '100vh' }}>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between', height: 62, bgcolor: '#fff', borderBottom: '1px solid', borderColor: 'divider', px: 2 }}>
          <Brand />
          <IconButton aria-label="Open menu" onClick={() => setMobileOpen(true)}><MenuIcon /></IconButton>
        </Box>
        <Box sx={{ px: { xs: 2, sm: 3, lg: 3.25 }, py: { xs: 2.25, md: 2.75 } }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
